import { buildSystemDefaultBiaImpactConfiguration } from './defaults.js';
import { BIA_GUIDANCE_SEVERITY_KEYS, BIA_SYSTEM_CATEGORY_KEYS } from './constants.js';
import { normalizeTimeHorizons } from './time-horizon.js';

/**
 * @typedef {Object} BiaCategoryConfig
 * @property {string} categoryKey
 * @property {number} [weight]
 * @property {{ tr?: string, en?: string }} [label]
 * @property {'system'|'custom'} [source]
 * @property {Partial<Record<'NEGLIGIBLE'|'MINOR'|'MODERATE'|'MAJOR'|'SEVERE', string>>} [severityDescriptions]
 */

/**
 * @typedef {Object} EffectiveBiaImpactConfiguration
 * @property {boolean} isCustomized
 * @property {BiaCategoryConfig[]} categories
 * @property {ReturnType<typeof normalizeTimeHorizons>} timeHorizons
 * @property {'SIMPLE_AVERAGE'|'WEIGHTED_AVERAGE'|'SINGLE_THRESHOLD'} calculationModel
 * @property {number} mtpdThreshold
 * @property {number} rtoThreshold
 * @property {number} rpoThreshold
 */

function normalizeCategoryLabel(label) {
    if (!label) return undefined;
    if (label instanceof Map) {
        const out = Object.fromEntries(label.entries());
        return Object.keys(out).length ? out : undefined;
    }
    if (typeof label === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(label)) {
            if (typeof v === 'string' && v.trim()) out[k] = v.trim();
        }
        return Object.keys(out).length ? out : undefined;
    }
    return undefined;
}

function normalizeSeverityDescriptions(descriptions) {
    if (!descriptions) return undefined;
    const source = descriptions instanceof Map
        ? Object.fromEntries(descriptions.entries())
        : descriptions;
    if (typeof source !== 'object') return undefined;

    const out = {};
    for (const key of BIA_GUIDANCE_SEVERITY_KEYS) {
        const value = source[key];
        if (typeof value === 'string' && value.trim()) {
            out[key] = value.trim();
        }
    }
    return Object.keys(out).length ? out : undefined;
}

function mapCategoryConfig(c) {
    /** @type {BiaCategoryConfig} */
    const row = { categoryKey: String(c.categoryKey) };
    if (c.weight != null && c.weight !== '') {
        const w = Number(c.weight);
        if (Number.isFinite(w) && w > 0) row.weight = w;
    }
    const label = normalizeCategoryLabel(c.label);
    if (label) row.label = label;
    if (c.source === 'system' || c.source === 'custom') {
        row.source = c.source;
    } else if (BIA_SYSTEM_CATEGORY_KEYS.includes(row.categoryKey)) {
        row.source = 'system';
    } else {
        row.source = 'custom';
    }
    const severityDescriptions = normalizeSeverityDescriptions(c.severityDescriptions);
    if (severityDescriptions) {
        row.severityDescriptions = severityDescriptions;
    }
    return row;
}

/**
 * @param {object|null|undefined} doc - raw DB document or plain object
 * @returns {EffectiveBiaImpactConfiguration}
 */
export function resolveBiaImpactConfiguration(doc) {
    const defaults = buildSystemDefaultBiaImpactConfiguration();
    if (!doc || doc.isCustomized !== true) {
        return { ...defaults, isCustomized: false };
    }

    const plain = typeof doc.toObject === 'function'
        ? doc.toObject({ flattenMaps: true, virtuals: false })
        : { ...doc };

    const categories = Array.isArray(plain.categories) && plain.categories.length > 0
        ? plain.categories.map(mapCategoryConfig)
        : defaults.categories;

    const timeHorizons = Array.isArray(plain.timeHorizons) && plain.timeHorizons.length > 0
        ? normalizeTimeHorizons(plain.timeHorizons)
        : defaults.timeHorizons;

    return {
        isCustomized: true,
        categories,
        timeHorizons,
        calculationModel: plain.calculationModel || defaults.calculationModel,
        mtpdThreshold: plain.mtpdThreshold ?? defaults.mtpdThreshold,
        rtoThreshold: plain.rtoThreshold ?? defaults.rtoThreshold,
        rpoThreshold: plain.rpoThreshold ?? defaults.rpoThreshold,
    };
}

/**
 * @param {string} categoryKey
 * @param {{ categories?: BiaCategoryConfig[] }|null|undefined} [config]
 * @returns {boolean}
 */
export function isValidCategoryKey(categoryKey, config) {
    if (config?.categories?.length) {
        return config.categories.some((c) => c.categoryKey === categoryKey);
    }
    return BIA_SYSTEM_CATEGORY_KEYS.includes(categoryKey);
}

export function isSystemCategoryKey(categoryKey) {
    return BIA_SYSTEM_CATEGORY_KEYS.includes(categoryKey);
}
